/**
 * TinyRiots Bundle Builder
 * Alpine.js component for the Build-a-Box bundle page.
 *
 * Architecture follows Lune & Wild's pattern:
 * - All config via data-* attributes on root element (Liquid generates, JS reads)
 * - Products discovered from DOM via data-bundle-product-handle + embedded JSON
 * - Filter values auto-discovered from product metafields
 * - Bundle state persisted in localStorage, with cart attributes as async fallback
 * - Cart only written at checkout via _rebuildCartForCheckout()
 * - Cart bubble updated via direct DOM manipulation (no Section Rendering API)
 * - Selling plans read from Shopify's selling_plan_allocations (embedded in DOM)
 * - Loop API for checkout transaction ID only
 */

/* ── Checkout Error UX ───────────────────────────────────
 * Short ref_id correlates the user-facing alert with the Sentry event
 * for support follow-up. In debug mode (window.__BUNDLE_DEBUG__) also
 * appends HTTP status so engineers can read failure type at a glance.
 */
function generateCheckoutRefId() {
  // 6-char base36 — short enough to type, long enough to be unique per session
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

function showCheckoutError(genericMessage, error, refId) {
  const isDebug = !!window.__BUNDLE_DEBUG__;
  const status = error?.status;
  let suffix = '';
  if (refId) suffix += ` (Ref: ${refId}`;
  if (isDebug && status) suffix += ` · status: ${status}`;
  if (refId) suffix += ')';
  alert(genericMessage + suffix);
}

/* ── Cart API Helpers ──────────────────────────────────── */

/* ── Retry-After parser (RFC 7231) ────────────────────────
 * Reads the Retry-After response header (sent by Shopify on 429/503)
 * and returns milliseconds to wait. Returns null if header is absent
 * or unparseable, in which case the caller falls back to exponential
 * backoff.
 */
function parseRetryAfterMs(headers) {
  if (!headers || typeof headers.get !== 'function') return null;
  const value = headers.get('Retry-After');
  if (!value) return null;
  // Either a non-negative integer (seconds) or an HTTP-date.
  const num = parseInt(value, 10);
  if (!Number.isNaN(num) && num >= 0) return num * 1000;
  const date = Date.parse(value);
  if (!Number.isNaN(date)) {
    const diff = date - Date.now();
    return diff > 0 ? diff : 0;
  }
  return null;
}

const CART_ADD_RETRYABLE_STATUSES = new Set([409, 429, 500, 502, 503, 504]);
const CART_ADD_MAX_ATTEMPTS = 5;

const CartAPI = {
  async _handleResponse(res) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data?.description || data?.message || `HTTP ${res.status}`;
      const error = new Error(msg);
      error.status = res.status;
      error.responseData = data;
      error.responseHeaders = res.headers;
      throw error;
    }
    return data;
  },

  async get() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return this._handleResponse(res);
  },

  async add(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    return this._handleResponse(res);
  },

  async clear() {
    const res = await fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
    });
    return this._handleResponse(res);
  },

  async update(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    return this._handleResponse(res);
  },

  /** Update cart attributes (fire-and-forget, for persistence fallback) */
  updateAttributes(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    }).catch(() => {});
  },
};

/* ── Cart Bubble Update (Direct DOM) ─────────────────────
 * Updates Dawn's #cart-icon-bubble directly from known state.
 * Zero API calls — purely DOM manipulation. */

function updateCartBubble(bundleCount) {
  const bubble = document.getElementById('cart-icon-bubble');
  if (!bubble) return;

  const baseCount = parseInt(bubble.dataset.baseCartCount || '0', 10);
  const total = baseCount + bundleCount;

  let countBubble = bubble.querySelector('.cart-count-bubble');

  if (total > 0) {
    if (!countBubble) {
      countBubble = document.createElement('div');
      countBubble.className = 'cart-count-bubble';
      const span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      countBubble.appendChild(span);
      bubble.appendChild(countBubble);
    }
    const countSpan = countBubble.querySelector('span[aria-hidden]');
    if (countSpan && total < 100) countSpan.textContent = total;
  } else {
    if (countBubble) countBubble.remove();
  }
}

/* ── Loop API Helpers ──────────────────────────────────── */

const LoopAPI = {
  async createTransaction(bundleId, payload) {
    if (!bundleId) return { txnId: null, status: 0, error: 'missing bundleId', body: null };
    try {
      const res = await fetch(
        `https://api.loopsubscriptions.com/storefront/2023-10/bundle/${bundleId}/transaction`,
        {
          method: 'POST',
          headers: { accept: 'application/json', 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        console.warn('[BundleBuilder] Loop API %d:', res.status, data);
      }
      const txnId = data?.data?.txnId || null;
      return {
        txnId,
        status: res.status,
        error: null,
        body: txnId ? null : JSON.stringify(data).slice(0, 300),
      };
    } catch (e) {
      console.warn('[BundleBuilder] Failed to create transaction:', e);
      return { txnId: null, status: null, error: (e && e.message) || String(e), body: null };
    }
  },
};

/* ── Utility Helpers ───────────────────────────────────── */

function formatMoney(cents) {
  if (typeof cents !== 'number' || isNaN(cents)) cents = 0;
  const format = window.shopMoneyFormat || '{{amount_with_comma_separator}}\u20ac';
  const amount = (cents / 100).toFixed(2);
  const parts = amount.split('.');
  const intPart = parts[0];
  const decPart = parts[1];
  const dotSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const commaSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return format
    .replace('{{amount_with_comma_separator}}', dotSep + ',' + decPart)
    .replace('{{amount_no_decimals_with_comma_separator}}', dotSep)
    .replace('{{amount}}', commaSep + '.' + decPart)
    .replace('{{amount_no_decimals}}', commaSep);
}

function parseJSON(str) {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function createCheckoutAbortError(message, code, metadata) {
  const error = new Error(message);
  error.name = 'BundleCheckoutAbortError';
  error.code = code || 'BUNDLE_CHECKOUT_ABORT';
  error.metadata = metadata || {};
  error.isExpected = true;
  return error;
}

/* Safe localStorage wrappers — prevents SecurityError in Safari private mode */
function safeGetItem(key) { try { return localStorage.getItem(key); } catch { return null; } }
function safeSetItem(key, value) { try { localStorage.setItem(key, value); } catch {} }
function safeRemoveItem(key) { try { localStorage.removeItem(key); } catch {} }

/* Cart attributes mirror — persists critical state server-side as fallback */
const _cartAttrMap = {
  'bundle-builder:items': '_bb_items',
  'bundle-builder:discountTiers': '_bb_discount_tiers',
  'bundle-builder:appliedDiscountCode': '_bb_applied_code',
  'bundle-builder:cartSync': '_bb_cart_sync',
  'bundle-builder:giftVariantId': '_bb_gift_variant_id',
  'bundle-builder:loopBundleId': '_bb_loop_bundle_id',
  'bundle-builder:sellingPlanId': '_bb_selling_plan_id',
  'bundle-builder:loopBundleName': '_bb_loop_bundle_name',
};

const _cartAttrWriteState = {
  pending: {},
  scheduled: false,
  queue: Promise.resolve(),
};

function queueCartAttrUpdate(attrs) {
  if (!attrs || Object.keys(attrs).length === 0) return;
  Object.assign(_cartAttrWriteState.pending, attrs);
  if (_cartAttrWriteState.scheduled) return;
  _cartAttrWriteState.scheduled = true;

  Promise.resolve().then(() => {
    _cartAttrWriteState.scheduled = false;
    const batch = _cartAttrWriteState.pending;
    _cartAttrWriteState.pending = {};
    _cartAttrWriteState.queue = _cartAttrWriteState.queue
      .catch(() => {})
      .then(() => CartAPI.updateAttributes(batch));
  });
}

/** Write to localStorage + async mirror to cart attributes */
function persistState(key, value) {
  safeSetItem(key, value);
  const attrKey = _cartAttrMap[key];
  if (attrKey) queueCartAttrUpdate({ [attrKey]: value || '' });
}

/** Read from localStorage, fallback to cart attributes cache */
function readState(key, cartAttrs) {
  const local = safeGetItem(key);
  if (local !== null) return local;
  const attrKey = _cartAttrMap[key];
  return attrKey && cartAttrs ? (cartAttrs[attrKey] || null) : null;
}

/** Clear all _bb_* cart attributes */
function clearCartAttrs() {
  const attrs = {};
  for (const k of Object.values(_cartAttrMap)) attrs[k] = '';
  queueCartAttrUpdate(attrs);
}

/* ── Main Alpine Component ─────────────────────────────── */

function bundleBuilder() {
  return {
    /* ── Configuration (from data-* attributes) ── */
    section_settings: {},
    bundle_targets: [],
    bundle_filters: [],
    groupMinPrices: [],

    /* ── Products (discovered from DOM) ── */
    products: [],
    _bundleHandles: null, // Set of product handles in the bundle collection

    /* ── Bundle State ── */
    items_added: [],
    item_count: 0,
    progressPercent: 0,
    achievedPercent: 0,
    firstTierPercent: 0,
    bundle_target_id: null,
    bundle_target: null,

    /* ── Pricing ── */
    total_price: 0,
    discounted_price: 0,
    final_price: 0,
    currentDiscount: 0,
    _loopMaxDiscount: 0,

    /* ── Shipping ── */
    freeShipping: false,  // true when active subscription tier has free_shipping flag
    shipping_cost: 0,     // in cents, from section_settings.shipping_cost

    /* ── Selling Plans ── */
    selling_plan_options: [],
    selected_selling_plan: null,
    selling_plan_id: null,

    /* ── Purchase Type ── */
    purchaseType: 'subscription', // 'subscription' or 'onetime' (overridden by data-default-purchase-type)

    /* ── Filters ── */
    active_filters: [],
    filters_with_values: [],
    openFilters: '',

    /* ── Quick-View ── */
    quickView: { open: false, product: null },
    qvActiveImage: 0,

    /* ── UI State ── */
    isAdding: false,
    loaded: false,
    pricesReady: false,
    _debug: false,
    mobileExpanded: false,
    open_groups: ['tiny-cremas'], // overridden by data-default-category
    activeGroup: '',  // overridden by data-default-category
    _filterScrollY: 0,
    _loopConfigPromise: null,

    /* ── Initialization ── */
    async init() {
      // On bfcache restore, resync from live cart API
      window.addEventListener('pageshow', (e) => {
        this._log('[BundleBuilder] pageshow fired, persisted=%s', e.persisted);
        if (!e.persisted) return;
        this.isAdding = false;
        this._resyncFromCart();
      });

      const $el = this.$el;

      // 1. Parse configuration
      this.section_settings = parseJSON($el.getAttribute('data-section-settings')) || {};
      this.bundle_targets = parseJSON($el.getAttribute('data-bundle-target')) || [];
      this.bundle_filters = parseJSON($el.getAttribute('data-bundle-filters')) || [];
      this.groupMinPrices = parseJSON($el.getAttribute('data-group-min-prices')) || [];
      this._debug = !!this.section_settings.debug;
      if (this._debug) safeSetItem('bundle-builder:debug', '1');
      else safeRemoveItem('bundle-builder:debug');

      // 1b. Apply merchant-configurable defaults
      const defaultCategory = $el.getAttribute('data-default-category') || 'none';
      // Open all product groups by default (accordion starts expanded)
      const allGroups = [...$el.querySelectorAll('[data-product-group]')].map(g => g.dataset.productGroup);
      this.open_groups = allGroups.length ? allGroups : (defaultCategory !== 'none' ? [defaultCategory] : []);
      this.activeGroup = defaultCategory === 'none' ? '' : defaultCategory;

      const defaultPurchase = $el.getAttribute('data-default-purchase-type') || 'subscription';
      const onetimeDisabled = $el.hasAttribute('data-onetime-disabled');
      this.purchaseType = onetimeDisabled ? 'subscription' : defaultPurchase;
      this.shipping_cost = this.section_settings.shipping_cost || 0;

      // Sort targets ascending by item count
      this.bundle_targets.sort((a, b) => a.settings.target - b.settings.target);

      // Set initial tier
      if (this.bundle_targets.length) {
        this.bundle_target_id = this.bundle_targets[0].id;
        this.bundle_target = this.bundle_targets[0].settings;
      }

      // 2. Discover products from DOM
      this.products = [...$el.querySelectorAll('[data-bundle-product-handle]')].map((element) => {
        const handle = element.getAttribute('data-bundle-product-handle');
        const dataEl = element.querySelector(`script[data-product-data="${handle}"]`);
        const data = dataEl ? parseJSON(dataEl.textContent) : {};
        return { element, handle, ...data };
      });

      // 2b. Build bundle handles set for cart restoration
      this._bundleHandles = new Set(this.products.map((p) => p.handle));

      // 2d. Build allergen handle→translated-name map for filter chip labels.
      // Filters match on the locale-stable metaobject handle, but the visible
      // chip label should use the translated `nombre` (from allergen_details/
      // libre_de_details), so EN shows "Egg" not the Spanish handle "Huevo".
      this.allergenNames = {};
      this.products.forEach((p) => {
        [...(p.allergen_details || []), ...(p.libre_de_details || [])].forEach((d) => {
          if (d && d.handle && d.name) this.allergenNames[d.handle] = d.name;
        });
      });

      // 2c. Extract selling plans from product DOM data (Shopify-native)
      const allAllocations = this.products.flatMap((p) => p.selling_plan_allocations || []);
      const uniquePlans = new Map();
      allAllocations.forEach((alloc) => {
        // Handle both nested (selling_plan.id) and flat (selling_plan_id) Liquid formats
        const sp = alloc.selling_plan || (alloc.selling_plan_id ? { id: alloc.selling_plan_id, name: '' } : null);
        if (sp && !uniquePlans.has(sp.id)) {
          uniquePlans.set(sp.id, {
            selling_plan: {
              id: sp.id,
              name: sp.name,
              price_adjustments: sp.price_adjustments || [],
            },
          });
        }
      });
      if (uniquePlans.size) {
        this.selling_plan_options = [...uniquePlans.values()];
        this.autoSelectSellingPlan();
      }
      this._log('[BundleBuilder] Selling plans: %d found, selling_plan_id: %s', this.selling_plan_options.length, this.selling_plan_id);

      // 3. Parse initial cart from server-rendered HTML (synchronous for fast first render)
      this._initialCart = parseJSON($el.getAttribute('data-initial-cart')) || [];
      this._cartAttrs = parseJSON($el.getAttribute('data-cart-attributes')) || {};
      this._log('[BundleBuilder] init: data-initial-cart=%d items, bundleHandles=%d, wentToCart=%s', this._initialCart.length, this._bundleHandles.size, readState('bundle-builder:wentToCart', this._cartAttrs));
      const nonBundleCount = this._initialCart
        .filter((item) => !item.properties?._bundleId && !this._bundleHandles.has(item.handle))
        .reduce((sum, item) => sum + item.quantity, 0);
      const cartBubble = document.getElementById('cart-icon-bubble');
      if (cartBubble) cartBubble.dataset.baseCartCount = nonBundleCount;

      // 4. Restore bundle state: cart is source of truth, localStorage is fallback
      this._restoreBundleState();

      // 5. Auto-discover filter values from product metafields
      this.filters_with_values = this.bundle_filters.map((filter) => {
        const [namespace, key] = filter.filter_metafield.split('.');
        const values = [
          ...new Set(
            this.products.flatMap((p) => {
              const val = p.metafields?.[namespace]?.[key];
              if (Array.isArray(val)) return val;
              if (typeof val === 'number' && val !== 0) return [String(val)];
              if (typeof val === 'string' && val) return [val];
              return [];
            })
          ),
        ].filter(Boolean);
        // Sort numeric-looking values (e.g. age months) numerically
        values.sort((a, b) => {
          const nA = Number(a), nB = Number(b);
          if (!isNaN(nA) && !isNaN(nB)) return nA - nB;
          return String(a).localeCompare(String(b));
        });
        return { ...filter, values };
      });

      // 6. Read Loop bundle props for supplementary data (box sizes)
      if (this.section_settings.loop_bundle_id) {
        this._readLoopBundleProps();
        this._loopConfigPromise = this._consumeLoopConfig();
      }

      // 6b. Persist config for cart page safety nets
      if (this.section_settings.gift_variant_id) {
        persistState('bundle-builder:giftVariantId', String(this.section_settings.gift_variant_id));
      }
      if (this.section_settings.loop_bundle_id) {
        persistState('bundle-builder:loopBundleId', this.section_settings.loop_bundle_id);
      }
      if (this.selling_plan_id) {
        persistState('bundle-builder:sellingPlanId', String(this.selling_plan_id));
      }
      if (this.section_settings.loop_bundle_name) {
        persistState('bundle-builder:loopBundleName', this.section_settings.loop_bundle_name);
      }

      // 7. Set up watchers + recalculate restored state
      this.$watch('items_added', () => this.recalculate());
      if (this.items_added.length) this.recalculate();

      // 7b. Hide groups with zero products on initial load (e.g. all out of stock)
      this.$nextTick(() => this._hideEmptyGroups());

      // 8. Scroll-spy: auto-highlight active category in nav as user scrolls
      //    Only activates after the user interacts (click on a category card/pill)
      //    to avoid preselecting the first visible group on page load.
      const groups = $el.querySelectorAll('.bundle-builder__group');
      this._scrollSpyEnabled = this.activeGroup !== '';
      if (groups.length > 1) {
        this._scrollObserver = new IntersectionObserver(
          (entries) => {
            if (!this._scrollSpyEnabled) return;
            for (const entry of entries) {
              if (entry.isIntersecting) {
                const groupId = entry.target.getAttribute('data-product-group');
                if (groupId) this.activeGroup = groupId;
              }
            }
          },
          { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
        );
        groups.forEach((group) => this._scrollObserver.observe(group));
      }

      // 9. Intercept cart link clicks to sync bundle items before navigation
      this._interceptCartNavigation();

      this.loaded = true;

      // 10. Async correction: fetch live cart and force-update if stale
      // data-initial-cart can be stale (bfcache, CDN cache, etc.)
      // Alpine doesn't await async init(), so this runs as fire-and-forget
      this._resyncFromCart();
      // Fallback: if resync takes too long, show prices anyway
      setTimeout(() => { if (!this.pricesReady) this.pricesReady = true; }, 2000);
    },

    /**
     * Restore bundle state. Uses a fingerprint to detect cart edits:
     * - If cart changed since last sync → user edited on /cart → prefer cart
     * - If cart unchanged → user may have edited in builder → prefer localStorage
     * - If cart emptied (fingerprint exists but cart empty) → clear state
     * - If cart empty + no fingerprint → fallback to localStorage
     */
    _restoreBundleState(forceCart = false) {
      const cartBundleItems = this._initialCart.filter(
        (item) => !!item.properties?._bundleId || this._bundleHandles.has(item.handle)
      );
      const savedFingerprint = readState('bundle-builder:cartSync', this._cartAttrs);
      this._log('[BundleBuilder] _restoreBundleState: %d bundle items in data-initial-cart, savedFingerprint=%s', cartBundleItems.length, savedFingerprint ? 'yes' : 'no');

      if (cartBundleItems.length) {
        // Cart has bundle items — build fingerprint to compare
        const cartFingerprint = cartBundleItems
          .map((ci) => `${ci.handle}:${ci.variant_id}:${ci.quantity}`)
          .sort()
          .join('|');

        if (!forceCart && savedFingerprint && cartFingerprint === savedFingerprint) {
          // Cart unchanged → localStorage may have evolved (user edited in builder)
          this._log('[BundleBuilder] _restoreBundleState: fingerprints match → trying localStorage');
          const restored = this._restoreFromStorage();
          if (restored) return;
        }

        // Cart changed, forced, or first sync → cart is source of truth
        const items = [];
        for (const ci of cartBundleItems) {
          if (!this._bundleHandles.has(ci.handle)) continue;
          const product = this.products.find((p) => p.handle === ci.handle);
          const price = product?.price || ci.price || 0;
          for (let i = 0; i < ci.quantity; i++) {
            items.push({ handle: ci.handle, variantId: ci.variant_id, price, quantity: 1 });
          }
        }
        if (items.length) {
          this.items_added = items;
          if (cartBundleItems.some((ci) => ci.selling_plan_allocation)) {
            this.purchaseType = 'subscription';
          } else {
            this.purchaseType = 'onetime';
          }
          persistState('bundle-builder:cartSync', cartFingerprint);
          persistState('bundle-builder:items', JSON.stringify({
            items, purchaseType: this.purchaseType, sellingPlanId: this.selling_plan_id,
          }));
          this._log('[BundleBuilder] _restoreBundleState: restored %d items from cart', items.length);
          return;
        }
      }

      if (savedFingerprint) {
        // Cart is EMPTY but we previously synced to it → user cleared the cart
        persistState('bundle-builder:items', '');
        persistState('bundle-builder:cartSync', '');
        this._log('[BundleBuilder] _restoreBundleState: cart emptied (fingerprint existed) → cleared');
        return;
      }

      // No bundle items in cart, no prior sync → fallback to localStorage
      this._log('[BundleBuilder] _restoreBundleState: no bundle items in cart, no fingerprint → trying localStorage');
      this._restoreFromStorage();
    },

    /** Restore from localStorage. Returns true if items were restored. */
    _restoreFromStorage() {
      try {
        const saved = parseJSON(readState('bundle-builder:items', this._cartAttrs));
        if (!saved) return false;
        // Restore purchaseType even if no items (user toggled before adding)
        if (saved.purchaseType) this.purchaseType = saved.purchaseType;
        if (saved.sellingPlanId) this.selling_plan_id = saved.sellingPlanId;
        const savedItems = Array.isArray(saved) ? saved : saved?.items;
        if (!savedItems?.length) return false;

        const valid = savedItems.filter((item) => this._bundleHandles.has(item.handle));
        if (valid.length) {
          this.items_added = valid;
          this._log('[BundleBuilder] _restoreFromStorage: restored %d items (%s)', valid.length, this.purchaseType);
          return true;
        }
      } catch (e) {
        this._warn('[BundleBuilder] Could not restore bundle state:', e);
      }
      return false;
    },

    /**
     * Re-fetch cart from API and update builder state directly.
     * - If live cart has bundle items → use them (cart wins over localStorage/data-initial-cart).
     * - If live cart has 0 bundle items → leave builder state as-is (user may be building,
     *   or /cart.js may return stale empty data during page transitions).
     */
    async _resyncFromCart() {
      try {
        const data = await CartAPI.get();
        const liveItems = data.items || [];
        this._log('[BundleBuilder] _resyncFromCart: /cart.js returned %d items', liveItems.length);

        const cartBundleItems = liveItems.filter(
          (item) => !!item.properties?._bundleId || this._bundleHandles.has(item.handle)
        );
        this._log('[BundleBuilder] _resyncFromCart: %d bundle items in live cart', cartBundleItems.length);

        if (cartBundleItems.length) {
          const items = [];
          for (const ci of cartBundleItems) {
            if (!this._bundleHandles.has(ci.handle)) continue;
            const product = this.products.find((p) => p.handle === ci.handle);
            const price = product?.price || ci.price || 0;
            for (let i = 0; i < ci.quantity; i++) {
              items.push({ handle: ci.handle, variantId: ci.variant_id, price, quantity: 1 });
            }
          }
          if (items.length) {
            this._log('[BundleBuilder] _resyncFromCart: updating items_added %d → %d', this.items_added.length, items.length);
            this.items_added = items;
            if (cartBundleItems.some((ci) => ci.selling_plan_allocation)) {
              this.purchaseType = 'subscription';
            } else {
              this.purchaseType = 'onetime';
            }
            // Sync fingerprint + localStorage to match live cart
            const fp = cartBundleItems
              .map((ci) => `${ci.handle}:${ci.variant_id}:${ci.quantity}`)
              .sort()
              .join('|');
            persistState('bundle-builder:cartSync', fp);
            persistState('bundle-builder:items', JSON.stringify({
              items, purchaseType: this.purchaseType, sellingPlanId: this.selling_plan_id,
            }));
            safeRemoveItem('bundle-builder:wentToCart');
          }
        }
        // If no bundle items in live cart, leave builder state as-is.
        // The /discount/ fetch fix ensures back-navigation loads a fresh page
        // with accurate data-initial-cart, so _restoreBundleState() handles it.
        this.recalculate();
        this.pricesReady = true;
      } catch (e) {
        this._warn('[BundleBuilder] Cart resync failed:', e);
        this.pricesReady = true;
      }
    },

    /** Save fingerprint of what we wrote to cart (must match format in _restoreBundleState). */
    _saveCartFingerprint() {
      // Group items by handle:variantId and sum quantities — same format as cart fingerprint
      const grouped = {};
      this.items_added.forEach((item) => {
        const key = `${item.handle}:${item.variantId}`;
        grouped[key] = (grouped[key] || 0) + 1;
      });
      const fingerprint = Object.entries(grouped)
        .map(([key, qty]) => `${key}:${qty}`)
        .sort()
        .join('|');
      persistState('bundle-builder:cartSync', fingerprint);
    },

    /** Save discount tiers so the cart page can auto-upgrade/downgrade codes. */
    _saveDiscountTiers() {
      if (!this._loopDiscounts?.length) return;
      const tiers = this._loopDiscounts
        .filter((d) => d.code && d.conditionType === 'QUANTITY')
        .map((d) => ({ minQty: d.minCartQuantity || 0, code: d.code }))
        .sort((a, b) => b.minQty - a.minQty);
      persistState('bundle-builder:discountTiers', JSON.stringify(tiers));
      // Also save full discounts (with IDs) for cart page bundle repair
      safeSetItem('bundle-builder:loopDiscounts', JSON.stringify(this._loopDiscounts));
      if (this._boxSizes?.length) {
        safeSetItem('bundle-builder:boxSizes', JSON.stringify(this._boxSizes));
      }
    },

    destroy() {
      if (this._scrollObserver) {
        this._scrollObserver.disconnect();
        this._scrollObserver = null;
      }
    },

    _readLoopBundleProps() {
      const props = window.loopBundleProps;
      if (!props) return;
      if (props.bundleSizes?.length) {
        this._boxSizes = props.bundleSizes;
      }
    },

    async _consumeLoopConfig() {
      if (!window.__loopBundleConfig) return;
      try {
        const data = await window.__loopBundleConfig;
        if (data) this._applyLoopConfig(data);
      } catch (err) {
        this._warn('[BundleBuilder] Loop config unavailable, using schema defaults:', err.message);
      }
    },

    _applyLoopConfig(data) {
      // Store box sizes for createTransaction at checkout
      if (data.boxSizes?.length) {
        const sizes = [...data.boxSizes].sort((a, b) => a.minSize - b.minSize);
        this._boxSizes = sizes.map((bs) => ({
          id: bs.id,
          minQuantity: bs.minSize,
          maxQuantity: bs.maxSize || bs.minSize,
        }));
      }

      // Override discount tiers from Loop API, preserve schema free-shipping tiers
      if (data.discounts?.length) {
        this._loopDiscounts = data.discounts;
        this._log('[BundleBuilder] Loop discounts loaded:', data.discounts.map((d) => `${d.name || d.id}: ${d.value}% (min ${d.minCartQuantity || 0} items)`));

        const sorted = [...data.discounts]
          .filter((d) => d.conditionType === 'QUANTITY')
          .sort((a, b) => (a.minCartQuantity || 0) - (b.minCartQuantity || 0));

        if (sorted.length) {
          // Preserve free-shipping tiers from schema that Loop doesn't provide
          const schemaFreeShippingTiers = this.bundle_targets.filter((t) => t.settings.free_shipping);

          const apiTargets = sorted.map((d) => ({
            id: d.id,
            settings: {
              target: d.minCartQuantity || 0,
              discount_percentage: d.value || 0,
              discount_amount: 0,
              free_shipping: !!d.freeShipping,
            },
          }));

          // Merge: API discount tiers + schema free-shipping tiers not already covered
          const apiTargetValues = new Set(apiTargets.map((t) => t.settings.target));
          const extraFsTiers = schemaFreeShippingTiers.filter((t) => !apiTargetValues.has(t.settings.target));

          this.bundle_targets = [...apiTargets, ...extraFsTiers]
            .sort((a, b) => a.settings.target - b.settings.target);

          this.bundle_target_id = this.bundle_targets[0].id;
          this.bundle_target = this.bundle_targets[0].settings;
          this._log('[BundleBuilder] bundle_targets merged (API + schema free-shipping):', this.bundle_targets.map((t) => `${t.settings.target} items → ${t.settings.discount_percentage}%${t.settings.free_shipping ? ' +free shipping' : ''}`));
        }
      } else {
        this._warn('[BundleBuilder] No discounts in Loop — using schema defaults');
      }

      this.recalculate();
    },

    async _ensureLoopCheckoutConfig() {
      if (!this.isSubscription || !this.section_settings.loop_bundle_id) return true;

      const hasRequiredConfig = () => Array.isArray(this._boxSizes) && this._boxSizes.length > 0;
      if (hasRequiredConfig()) return true;

      this._readLoopBundleProps();

      for (let attempt = 1; attempt <= 3; attempt += 1) {
        this._log('[BundleBuilder] Waiting for Loop checkout config (attempt %d)', attempt);
        console.info('[BundleBuilder] Waiting for Loop checkout config', {
          attempt,
          hasBoxSizes: !!(this._boxSizes && this._boxSizes.length),
          hasDiscounts: !!(this._loopDiscounts && this._loopDiscounts.length),
          itemCount: this.item_count,
        });
        window.TR.breadcrumb('Waiting for Loop checkout config', {
          attempt,
          hasBoxSizes: !!(this._boxSizes && this._boxSizes.length),
          hasDiscounts: !!(this._loopDiscounts && this._loopDiscounts.length),
          itemCount: this.item_count,
        }, 'state');

        try {
          this._loopConfigPromise = this._consumeLoopConfig();
          await this._loopConfigPromise;
        } catch (e) {
          this._warn('[BundleBuilder] Loop config wait failed (attempt %d):', attempt, e);
          console.warn('[BundleBuilder] Loop config wait failed', { attempt, error: e?.message || String(e) });
        }

        this._readLoopBundleProps();
        if (hasRequiredConfig()) return true;
        if (attempt < 3) {
          await new Promise((resolve) => setTimeout(resolve, attempt * 150));
        }
      }

      this._warn('[BundleBuilder] Loop checkout config still missing after retries', {
        itemCount: this.item_count,
        hasBoxSizes: !!(this._boxSizes && this._boxSizes.length),
        hasDiscounts: !!(this._loopDiscounts && this._loopDiscounts.length),
      });
      console.warn('[BundleBuilder] Loop checkout config still missing after retries', {
        itemCount: this.item_count,
        hasBoxSizes: !!(this._boxSizes && this._boxSizes.length),
        hasDiscounts: !!(this._loopDiscounts && this._loopDiscounts.length),
      });
      window.TR.breadcrumb('Loop checkout config missing after retries', {
        itemCount: this.item_count,
        hasBoxSizes: !!(this._boxSizes && this._boxSizes.length),
        hasDiscounts: !!(this._loopDiscounts && this._loopDiscounts.length),
      }, 'error');
      return false;
    },

    autoSelectSellingPlan() {
      if (this.selling_plan_options.length && !this.selected_selling_plan) {
        const first = this.selling_plan_options[0].selling_plan;
        this.selected_selling_plan = first.name;
        this.selling_plan_id = first.id;
      }
    },

    /* ── Accordion / Group Methods ── */

    toggleGroup(handle) {
      const idx = this.open_groups.indexOf(handle);
      if (idx !== -1) {
        this.open_groups.splice(idx, 1);
      } else {
        this.open_groups.push(handle);
      }
    },

    isGroupOpen(handle) {
      return this.open_groups.includes(handle);
    },

    scrollToGroup(handle) {
      // Enable scroll-spy once user interacts with categories
      this._scrollSpyEnabled = true;
      // Open the group if closed
      if (!this.isGroupOpen(handle)) {
        this.open_groups.push(handle);
      }
      this.activeGroup = handle;

      // Scroll to group element
      this.$nextTick(() => {
        const el = document.getElementById(`group-${handle}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    /* ── Quick-View Methods ── */

    openQuickView(handle) {
      const product = this.products.find((p) => p.handle === handle);
      if (!product) return;
      this.quickView.product = product;
      this.qvActiveImage = 0;
      this.quickView.open = true;
      this.$nextTick(() => {
        const inner = document.querySelector('.qv-dialog__inner');
        if (inner) inner.scrollTop = 0;
      });
    },

    closeQuickView() {
      this.quickView.open = false;
    },

    /* ── Add / Remove Methods ── */

    addToBundle({ handle, variantId, price }) {
      const product = this.products.find((p) => p.handle === handle && p.variant_id === variantId);
      if (product && !product.available) return;
      if (this.isAtStockLimit(handle, variantId)) return;
      this.items_added = [...this.items_added, { handle, variantId, price, quantity: 1 }];
    },

    removeFromBundle({ handle, variantId }) {
      // Find last instance (reverse search for LIFO removal)
      let idx = -1;
      for (let i = this.items_added.length - 1; i >= 0; i--) {
        if (this.items_added[i].handle === handle && this.items_added[i].variantId === variantId) {
          idx = i;
          break;
        }
      }
      if (idx === -1) return;

      this.items_added.splice(idx, 1);
      this.items_added = [...this.items_added];
    },

    async clearBundle() {
      this.items_added = [];
      // If the Shopify cart has bundle items (from a previous checkout), clear it too.
      // Otherwise on next page load _restoreBundleState() would restore from cart.
      const hasBundleInCart = this._initialCart.some(
        (item) => !!item.properties?._bundleId || this._bundleHandles.has(item.handle)
      );
      if (hasBundleInCart) {
        try {
          await CartAPI.clear();
          persistState('bundle-builder:cartSync', '');
        } catch {}
      }
    },

    getItemCount(handle, variantId) {
      return this.items_added.filter(
        (item) => item.handle === handle && item.variantId === variantId
      ).length;
    },

    getMaxQuantity(handle, variantId) {
      const product = this.products.find((p) => p.handle === handle && p.variant_id === variantId);
      if (!product) return 0;
      if (!product.inventory_management || product.inventory_policy === 'continue') return Infinity;
      return Math.max(0, product.inventory_quantity);
    },

    isAtStockLimit(handle, variantId) {
      return this.getItemCount(handle, variantId) >= this.getMaxQuantity(handle, variantId);
    },

    /* ── Pricing Recalculation ── */

    recalculate() {
      this.item_count = this.items_added.length;
      const maxTarget = this.bundle_targets[this.bundle_targets.length - 1]?.settings?.target;
      this.progressPercent = maxTarget ? Math.min(100, Math.round((this.item_count / maxTarget) * 100)) : 0;
      const firstTarget = this.bundle_targets[0]?.settings?.target || 0;
      this.firstTierPercent = maxTarget ? Math.min(100, Math.round((firstTarget / maxTarget) * 100)) : 0;

      // Green layer: width of highest achieved milestone (8/10/12)
      const achievedTarget = this.bundle_targets
        .map(t => t.settings.target)
        .filter(t => this.item_count >= t)
        .pop() || 0;
      this.achievedPercent = maxTarget ? Math.min(100, Math.round((achievedTarget / maxTarget) * 100)) : 0;

      this.total_price = this.items_added.reduce((sum, item) => sum + item.price, 0);

      // Find achieved tier (highest where target <= item_count)
      const achieved = [...this.bundle_targets]
        .sort((a, b) => b.settings.target - a.settings.target)
        .find((t) => t.settings.target <= this.item_count);

      // Subscription discount: achieved tier, or base tier as floor (10% from item 0)
      const achievedTierDiscount = achieved?.settings?.discount_percentage || 0;
      const baseTierDiscount = this.bundle_targets[0]?.settings?.discount_percentage || 0;
      const discount_pct = this.isSubscription ? Math.max(baseTierDiscount, achievedTierDiscount) : 0;
      this.currentDiscount = discount_pct;
      // Per-unit floor rounding to match Shopify's discount calculation
      this.discounted_price = discount_pct > 0
        ? this.items_added.reduce((sum, item) => sum + item.price - Math.floor(item.price * discount_pct / 100), 0)
        : this.total_price;
      this.final_price = this.discounted_price;

      // Shipping: free when subscription and an achieved tier carries the free_shipping flag
      this.freeShipping = this.isSubscription
        ? this.bundle_targets.some((t) => t.settings.free_shipping && t.settings.target <= this.item_count)
        : false;

      // Auto-update bundle target based on item count
      this.updateBundleTarget();

      // Persist to localStorage + cart attributes (zero page-blocking API calls)
      persistState('bundle-builder:items', JSON.stringify({
        items: this.items_added,
        purchaseType: this.purchaseType,
        sellingPlanId: this.selling_plan_id,
      }));
      updateCartBubble(this.item_count);
    },

    updateBundleTarget() {
      // Find the appropriate tier for current count
      const appropriate = [...this.bundle_targets]
        .sort((a, b) => a.settings.target - b.settings.target)
        .find((t) => t.settings.target >= this.item_count);

      if (appropriate) {
        this.bundle_target = appropriate.settings;
        this.bundle_target_id = appropriate.id;
      } else if (this.bundle_targets.length) {
        // Over max tier — stick to highest
        const highest = this.bundle_targets[this.bundle_targets.length - 1];
        this.bundle_target = highest.settings;
        this.bundle_target_id = highest.id;
      }
    },

    getDiscountedPrice(priceInCents) {
      if (!this.isSubscription) return priceInCents;
      const pct = this.currentDiscount;
      return pct > 0 ? priceInCents - Math.floor(priceInCents * pct / 100) : priceInCents;
    },

    /* ── Computed Getters ── */

    get isBundleComplete() {
      const firstTarget = this.bundle_targets[0];
      return firstTarget && this.item_count >= firstTarget.settings.target;
    },

    get remainingItems() {
      if (!this.bundle_target?.target) return 0;
      return Math.max(0, this.bundle_target.target - this.item_count);
    },

    get nextTierDiscount() {
      const nextTier = [...this.bundle_targets]
        .sort((a, b) => a.settings.target - b.settings.target)
        .find((t) => t.settings.target > this.item_count);
      return nextTier?.settings?.discount_percentage || 0;
    },

    get nextTierTarget() {
      const nextTier = [...this.bundle_targets]
        .sort((a, b) => a.settings.target - b.settings.target)
        .find((t) => t.settings.target > this.item_count);
      return nextTier?.settings?.target || 0;
    },

    // Returns target count of the next free-shipping tier ahead (0 if none or already free)
    get nextTierFreeShipping() {
      if (this.freeShipping) return 0;
      const fsTier = [...this.bundle_targets]
        .sort((a, b) => a.settings.target - b.settings.target)
        .find((t) => t.settings.free_shipping && t.settings.target > this.item_count);
      return fsTier ? fsTier.settings.target : 0;
    },

    // Maximum discount % across all tiers (for "Hasta -X%" badge)
    get maxTierDiscount() {
      return Math.max(0, ...this.bundle_targets.map((t) => t.settings.discount_percentage || 0));
    },

    // Subscription discount % — always computed regardless of selected purchaseType
    // Used to display subscription price even when one-time is selected
    get subscriptionDiscount() {
      const achieved = [...this.bundle_targets]
        .sort((a, b) => b.settings.target - a.settings.target)
        .find((t) => t.settings.target <= this.item_count);
      // Base tier discount as floor (e.g. 10% even at 0 items)
      const base = this.bundle_targets[0]?.settings?.discount_percentage || 0;
      return Math.max(base, achieved?.settings?.discount_percentage || 0);
    },

    get subscriptionPrice() {
      const pct = this.subscriptionDiscount;
      if (!pct) return this.total_price;
      return this.items_added.reduce((sum, item) => sum + item.price - Math.floor(item.price * pct / 100), 0);
    },

    groupMinPrice(index) {
      const baseCents = this.groupMinPrices[index];
      if (!baseCents) return '';
      const pct = this.currentDiscount;
      const discounted = pct > 0 ? baseCents - Math.floor(baseCents * pct / 100) : baseCents;
      return formatMoney(discounted);
    },

    get activeFilterCount() {
      return this.active_filters.length;
    },

    get itemSummary() {
      return this.items_added.reduce((acc, item) => {
        const existing = acc.find((s) => s.variantId === item.variantId);
        if (existing) {
          existing.quantity += 1;
        } else {
          const product = this.products.find((p) => p.handle === item.handle);
          acc.push({
            handle: item.handle,
            variantId: item.variantId,
            title: product?.title || item.handle,
            image: product?.image || '',
            price: item.price,
            quantity: 1,
          });
        }
        return acc;
      }, []);
    },

    get mobileGridCols() {
      return 8;
    },

    get mobileEmptySlots() {
      const cols = this.mobileGridCols;
      // If unique dishes already fill a complete row, no empty slots
      if (this.itemSummary.length >= cols) return 0;
      return cols - this.itemSummary.length;
    },

    /* ── Filter Methods ── */

    toggleFilter(index, value) {
      const existing = this.active_filters.findIndex(
        (f) => f.index === index && f.value === value
      );
      if (existing !== -1) {
        this.active_filters.splice(existing, 1);
      } else {
        this.active_filters.push({ index, value });
      }
      this.active_filters = [...this.active_filters];
      this.applyFilters();
    },

    isFilterActive(index, value) {
      return this.active_filters.some((f) => f.index === index && f.value === value);
    },

    getFilterCount(filterIndex) {
      return this.active_filters.filter((f) => f.index === filterIndex).length;
    },

    clearFilterGroup(filterIndex) {
      this.active_filters = this.active_filters.filter((f) => f.index !== filterIndex);
      this.applyFilters();
    },

    formatFilterValue(filter, value) {
      if (filter.value_format === 'age_months') {
        const n = Number(value);
        if (!n || isNaN(n)) return String(value);
        const suffix = (window.bundleI18n && window.bundleI18n.ageMonthsSuffix) || '+[n] meses';
        return suffix.replace('[n]', n);
      }
      const s = String(value);
      // Allergen/libre_de chips: show the translated metaobject name, not the
      // locale-stable handle. Falls back to capitalized handle if unmapped.
      if (this.allergenNames && this.allergenNames[s]) return this.allergenNames[s];
      return s.charAt(0).toUpperCase() + s.slice(1);
    },

    clearFilters() {
      this.active_filters = [];
      this.applyFilters();
    },

    applyFilters() {
      if (this.active_filters.length === 0) {
        // Show all products
        this.products.forEach((p) => p.element.classList.remove('bundle-product-card--hidden'));
        this._hideEmptyGroups();
        return;
      }

      // Group active filters by index
      const grouped = {};
      this.active_filters.forEach((f) => {
        if (!grouped[f.index]) grouped[f.index] = [];
        grouped[f.index].push(f.value);
      });

      this.products.forEach((product) => {
        const visible = Object.entries(grouped).every(([filterIndex, filterValues]) => {
          const filter = this.bundle_filters[Number(filterIndex)];
          if (!filter) return false;

          const [namespace, key] = filter.filter_metafield.split('.');
          let productValues = product.metafields?.[namespace]?.[key];

          // Normalize to array of strings
          if (!Array.isArray(productValues)) {
            if (typeof productValues === 'number') productValues = [String(productValues)];
            else productValues = typeof productValues === 'string' && productValues ? [productValues] : [];
          }

          if (filter.filter_type === 'exclusion') {
            // EXCLUSION: product must NOT contain ANY selected value
            return filterValues.every(
              (v) => !productValues.some((pv) => pv.toLowerCase() === v.toLowerCase())
            );
          } else if (filter.value_format === 'age_months') {
            // AGE (cumulative): product edad >= selected minimum
            // e.g. selecting "+6 meses" shows products with edad 6, 9, 10
            return filterValues.some((v) =>
              productValues.some((pv) => Number(pv) >= Number(v))
            );
          } else if (filter.filter_match === 'all') {
            // INCLUSION (ALL): product must contain ALL selected values
            return filterValues.every(
              (v) => productValues.some((pv) => pv.toLowerCase() === v.toLowerCase())
            );
          } else {
            // INCLUSION (ANY): product must match at least ONE selected value
            return filterValues.some((v) =>
              productValues.some((pv) => pv.toLowerCase() === v.toLowerCase())
            );
          }
        });

        product.element.classList.toggle('bundle-product-card--hidden', !visible);
      });

      this._hideEmptyGroups();
    },

    /** Hide groups where all product cards are hidden or absent */
    _hideEmptyGroups() {
      const run = () => {
        this.$el.querySelectorAll('[data-product-group]').forEach((group) => {
          const total = group.querySelectorAll('[data-bundle-product-handle]').length;
          const hidden = group.querySelectorAll('.bundle-product-card--hidden').length;
          group.classList.toggle('bundle-builder__group--hidden', total === 0 || hidden === total);
        });
      };
      run();
      this.$nextTick(() => run());
    },

    /* ── Purchase Type ── */

    setPurchaseType(type) {
      if (type === 'onetime' && this.$el.hasAttribute('data-onetime-disabled')) return;
      this.purchaseType = type;
      this.recalculate();
    },

    get isSubscription() {
      return this.purchaseType === 'subscription';
    },

    /* ── Cart & Checkout Flow ── */

    _snapshotCartItems(cartData) {
      if (!Array.isArray(cartData?.items)) return [];

      return cartData.items
        .map((item) => {
          const variantId = Number(item.variant_id || item.id);
          if (!variantId || !item.quantity) return null;

          const snapshotItem = {
            id: variantId,
            quantity: item.quantity,
          };

          if (item.properties && Object.keys(item.properties).length) {
            snapshotItem.properties = item.properties;
          }

          const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
          if (sellingPlanId) {
            snapshotItem.selling_plan = sellingPlanId;
          }

          return snapshotItem;
        })
        .filter(Boolean);
    },

    async _restoreCartSnapshot(snapshotItems, context) {
      if (!Array.isArray(snapshotItems) || snapshotItems.length === 0) {
        this._log('[BundleBuilder] No previous cart snapshot to restore (%s)', context);
        return;
      }

      this._warn('[BundleBuilder] Restoring previous cart snapshot after checkout failure (%s)', context);
      console.warn('[BundleBuilder] Restoring previous cart snapshot after checkout failure', {
        context,
        itemCount: snapshotItems.length,
      });

      try {
        await CartAPI.add(snapshotItems);
        this._log('[BundleBuilder] Previous cart snapshot restored (%s)', context);
        console.info('[BundleBuilder] Previous cart snapshot restored', {
          context,
          itemCount: snapshotItems.length,
        });
        window.TR.breadcrumb('Previous cart snapshot restored', {
          context,
          itemCount: snapshotItems.length,
        }, 'state');
      } catch (restoreError) {
        this._warn('[BundleBuilder] Failed to restore previous cart snapshot:', restoreError);
        console.warn('[BundleBuilder] Failed to restore previous cart snapshot', {
          context,
          error: restoreError?.message || String(restoreError),
          status: restoreError?.status,
          responseData: restoreError?.responseData || null,
        });
        window.TR.error(restoreError, {
          context: 'restore-cart-snapshot',
          section: 'bundle',
          data: {
            context,
            snapshotItems,
            status: restoreError?.status || null,
            responseData: restoreError?.responseData || null,
          },
        });
      }
    },

    /**
     * Rebuild cart for checkout: creates the Loop transaction first (when needed),
     * then clears and re-adds all items with proper selling plan and bundle props.
     */
    async _rebuildCartForCheckout() {
      const refId = generateCheckoutRefId();
      this._checkoutRefId = refId;
      // Cart mutation lock — suppresses the discount-heal handler in main-cart-footer
      // (and any other cartUpdate-driven cart writer) while we rebuild. Long enough to
      // cover createTransaction + clear + add retries (worst case ~40s). Released in finally.
      window.__bundleCartLockUntil = Date.now() + 45000;
      window.TR.breadcrumb('Checkout started', { itemCount: this.item_count, purchaseType: this.purchaseType, isSubscription: this.isSubscription, refId }, 'state');

      try {
      // Pre-flight validation: subscription requires a resolved selling_plan_id.
      // If the variant pool has zero allocations Loop never seeded one and we'd send
      // items without selling_plan, which Shopify silently accepts as one-time —
      // wrong intent, no error. Abort early with a clear message.
      if (this.isSubscription && !this.selling_plan_id) {
        this._warn('[BundleBuilder] Subscription selected but selling_plan_id is null');
        window.TR.error(new Error('Subscription checkout without selling_plan_id'), {
          context: 'bundle-payload-validation',
          section: 'bundle',
          data: {
            refId,
            itemCount: this.item_count,
            purchaseType: this.purchaseType,
            sellingPlanOptionsCount: (this.selling_plan_options && this.selling_plan_options.length) || 0,
          },
        });
        showCheckoutError((window.bundleI18n && window.bundleI18n.checkoutErrorSubscription) || 'No hemos podido configurar tu suscripción. Por favor, recarga la página o contáctanos.', null, refId);
        throw createCheckoutAbortError('Subscription without selling_plan_id', 'SELLING_PLAN_MISSING', { refId });
      }

      const variantCounts = {};
      this.items_added.forEach((item) => {
        variantCounts[item.variantId] = (variantCounts[item.variantId] || 0) + 1;
      });
      let previousCartSnapshot = [];

      let txnId = null;
      const bundleId = this.section_settings.loop_bundle_id;
      const bundleName = this.section_settings.loop_bundle_name || '';

      if (this.isSubscription && bundleId) {
        const loopConfigReady = await this._ensureLoopCheckoutConfig();
        if (!loopConfigReady) {
          alert((window.bundleI18n && window.bundleI18n.checkoutErrorLoopConfig) || 'Error cargando la configuracion de la suscripcion. Por favor, espera un momento e intentalo de nuevo.');
          throw createCheckoutAbortError('Loop checkout config unavailable', 'LOOP_CONFIG_UNAVAILABLE', {
            itemCount: this.item_count,
            hasBoxSizes: !!(this._boxSizes && this._boxSizes.length),
            hasDiscounts: !!(this._loopDiscounts && this._loopDiscounts.length),
          });
        }

        const productVariants = Object.entries(variantCounts).map(([id, qty]) => ({
          shopifyId: Number(id),
          quantity: qty,
        }));
        let boxSizeId = null;
        if (this._boxSizes?.length) {
          const matchingSize = this._boxSizes.find(
            (bs) => bs.minQuantity <= this.item_count && bs.maxQuantity >= this.item_count
          );
          boxSizeId = matchingSize?.id || this._boxSizes[0]?.id;
        }

        // Find the best matching discount for the current item count
        let discountId = null;
        this._activeDiscountCode = null;
        if (this._loopDiscounts?.length) {
          const eligible = this._loopDiscounts
            .filter((d) => {
              if (d.purchaseType && d.purchaseType !== 'ALL' && d.purchaseType !== 'SUBSCRIPTION') return false;
              if (d.conditionType === 'QUANTITY') return this.item_count >= (d.minCartQuantity || 0);
              if (d.conditionType === 'PRICE') return this.total_price >= (d.minCartValue || 0);
              return true;
            })
            .sort((a, b) => (b.minCartQuantity || 0) - (a.minCartQuantity || 0));
          if (eligible.length) {
            discountId = eligible[0].id;
            this._activeDiscountCode = eligible[0].code;
          }
        }

        // Pre-check: was Loop API reachable during page load?
        if (!this._loopDiscounts?.length && !this._boxSizes?.length) {
          this._warn('[BundleBuilder] Loop API was unreachable during init — createTransaction may fail');
          window.TR.breadcrumb('Loop API unreachable at init', { bundleId }, 'state');
        }

        if (!boxSizeId) {
          this._warn('[BundleBuilder] Aborting checkout: boxSizeId is missing after Loop config wait', {
            itemCount: this.item_count,
            boxSizes: this._boxSizes,
            discountsLoaded: !!(this._loopDiscounts && this._loopDiscounts.length),
          });
          console.warn('[BundleBuilder] Aborting checkout: boxSizeId is missing after Loop config wait', {
            itemCount: this.item_count,
            boxSizes: this._boxSizes,
            discountsLoaded: !!(this._loopDiscounts && this._loopDiscounts.length),
          });
          window.TR.error(new Error('Loop checkout config missing boxSizeId'), {
            section: 'bundle',
            data: {
              refId,
              itemCount: this.item_count,
              sellingPlanId: this.selling_plan_id,
              boxSizesLoaded: !!(this._boxSizes && this._boxSizes.length),
              discountsLoaded: !!(this._loopDiscounts && this._loopDiscounts.length),
            },
          });
          showCheckoutError((window.bundleI18n && window.bundleI18n.checkoutErrorBoxConfig) || 'Error cargando la configuracion del pack. Por favor, espera un momento e intentalo de nuevo.', null, refId);
          throw createCheckoutAbortError('Loop checkout config missing boxSizeId', 'LOOP_BOX_SIZE_MISSING', {
            refId,
            itemCount: this.item_count,
            sellingPlanId: this.selling_plan_id,
          });
        }

        // Retry createTransaction on transient failures before giving up.
        // Do not clear the Shopify cart until we have a valid txnId.
        // No retry on deterministic 4xx — it would fail identically.
        const txnPayload = {
          boxSizeId,
          discountId,
          sellingPlanShopifyId: this.selling_plan_id,
          quantity: 1,
          productVariants,
        };
        const isRetryable = (r) => r.status === null || r.status === 429 || r.status >= 500;
        const attemptResults = [];
        let loopResult = await LoopAPI.createTransaction(bundleId, txnPayload);
        attemptResults.push(loopResult);
        while (!loopResult.txnId && attemptResults.length < 3 && isRetryable(loopResult)) {
          const backoffMs = 1000 * attemptResults.length;
          this._warn('[BundleBuilder] Retrying createTransaction (attempt %d)...', attemptResults.length + 1);
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
          loopResult = await LoopAPI.createTransaction(bundleId, txnPayload);
          attemptResults.push(loopResult);
        }
        if (!loopResult.txnId) {
          this._warn('[BundleBuilder] createTransaction failed after %d attempts — blocking checkout', attemptResults.length);
          window.TR.error(new Error('createTransaction failed after retries'), {
            context: 'loop-create-transaction',
            section: 'bundle',
            data: {
              refId,
              itemCount: this.item_count,
              sellingPlanId: this.selling_plan_id,
              boxSizeId,
              discountId,
              attempts: attemptResults.length,
              status: loopResult.status,
              errorMessage: loopResult.error,
              bodySnippet: loopResult.body,
              online: navigator.onLine,
            },
          });
          showCheckoutError((window.bundleI18n && window.bundleI18n.checkoutErrorTransaction) || 'Error al procesar tu suscripción. Por favor, inténtalo de nuevo.', { status: loopResult.status }, refId);
          throw createCheckoutAbortError('createTransaction failed after retries', 'LOOP_TRANSACTION_FAILED', {
            refId,
            itemCount: this.item_count,
            sellingPlanId: this.selling_plan_id,
            boxSizeId,
            discountId,
            status: loopResult.status,
          });
        }
        txnId = loopResult.txnId;
        window.TR.breadcrumb('Transaction created', { txnId, discountId }, 'state');
        this._log('[BundleBuilder] Checkout: txnId=%s, discountId=%s, items=%d', txnId, discountId, Object.keys(variantCounts).length);
      } else {
        this._log('[BundleBuilder] Checkout: one-time, items=%d', Object.keys(variantCounts).length);
      }

      // Build items with bundle properties when txnId is available
      const items = Object.entries(variantCounts).map(([id, qty]) => {
        const properties = { _source: 'storefront' };
        if (txnId) {
          properties._bundleId = txnId;
          if (bundleName) properties._bundleName = bundleName;
        }
        const item = { id: Number(id), quantity: qty, properties };
        if (this.isSubscription && this.selling_plan_id) {
          item.selling_plan = this.selling_plan_id;
        }
        return item;
      });

      // Auto-inject gift product for subscriptions (configured via theme editor)
      const giftVariantId = this.section_settings.gift_variant_id;
      if (this.isSubscription && giftVariantId) {
        const giftProps = { _isGift: 'true', _source: 'storefront' };
        if (txnId) {
          giftProps._bundleId = txnId;
          if (bundleName) giftProps._bundleName = bundleName;
        }
        items.push({ id: giftVariantId, quantity: 1, properties: giftProps });
        this._log('[BundleBuilder] Gift injected: variant=%s, txnId=%s', giftVariantId, txnId || 'none');
      } else if (this.isSubscription) {
        this._warn('[BundleBuilder] No gift_variant_id configured — subscription without gift');
        window.TR.error(new Error('Subscription checkout without gift — gift_variant_id missing'), { context: 'bundle-checkout' });
      }

      // Validation: every item must have a numeric, positive variant id.
      // Defends against Number(NaN) results (variant_id was a non-numeric string)
      // and zero/negative ids that would yield opaque Shopify rejections.
      const badItem = items.find((it) => !Number.isFinite(it.id) || it.id <= 0);
      if (badItem) {
        this._warn('[BundleBuilder] Invalid variant id in items:', badItem);
        window.TR.error(new Error('Invalid variant id in cart items'), {
          context: 'bundle-payload-validation',
          section: 'bundle',
          data: { refId, badItem, items },
        });
        showCheckoutError((window.bundleI18n && window.bundleI18n.checkoutErrorVariant) || 'Hay un producto con un identificador no válido. Por favor, recarga la página.', null, refId);
        throw createCheckoutAbortError('Invalid variant id', 'INVALID_VARIANT_ID', { refId, badItem });
      }

      try {
        const liveCart = await CartAPI.get();
        previousCartSnapshot = this._snapshotCartItems(liveCart);
      } catch (e) {
        this._warn('[BundleBuilder] Could not snapshot cart before rebuild:', e);
        console.warn('[BundleBuilder] Could not snapshot cart before rebuild', {
          error: e?.message || String(e),
          status: e?.status,
          responseData: e?.responseData || null,
        });
      }

      let cartCleared = false;
      try {
        await CartAPI.clear();
        cartCleared = true;
      } catch (e) {
        this._warn('[BundleBuilder] Cart clear failed:', e);
        throw createCheckoutAbortError('Cart clear failed before bundle rebuild', 'CART_CLEAR_FAILED', {
          status: e?.status || null,
          responseData: e?.responseData || null,
        });
      }

      // Retry cart add on transient statuses: 409 (race), 429 (rate limit), 5xx (Shopify hiccups).
      // Honors the Retry-After header for 429/503; otherwise exponential backoff capped at 4s.
      let cartAddError = null;
      for (let attempt = 1; attempt <= CART_ADD_MAX_ATTEMPTS; attempt++) {
        try {
          await CartAPI.add(items);
          cartAddError = null;
          break;
        } catch (e) {
          cartAddError = e;
          const status = e?.status || null;
          window.TR.breadcrumb('cart-add attempt failed', { attempt, status, refId }, 'request');
          if (CART_ADD_RETRYABLE_STATUSES.has(status) && attempt < CART_ADD_MAX_ATTEMPTS) {
            const retryAfterMs = parseRetryAfterMs(e?.responseHeaders);
            const backoffMs = retryAfterMs != null
              ? Math.min(retryAfterMs, 10000) // cap server hints at 10s to avoid hanging UX
              : Math.min(Math.pow(2, attempt - 1) * 200, 4000);
            this._warn(
              '[BundleBuilder] Cart add status %s (attempt %d/%d), retrying in %dms (%s)...',
              status, attempt, CART_ADD_MAX_ATTEMPTS, backoffMs, retryAfterMs != null ? 'Retry-After' : 'exp-backoff',
            );
            await new Promise((r) => setTimeout(r, backoffMs));
          } else {
            break;
          }
        }
      }

      if (cartAddError) {
        this._warn('[BundleBuilder] Cart add failed during bundle rebuild:', cartAddError);
        console.warn('[BundleBuilder] Cart add failed during bundle rebuild', {
          refId,
          error: cartAddError?.message || String(cartAddError),
          status: cartAddError?.status,
          responseData: cartAddError?.responseData || null,
          items,
        });
        window.TR.error(cartAddError, {
          context: 'bundle-cart-add',
          section: 'bundle',
          data: {
            refId,
            itemCount: this.item_count,
            purchaseType: this.purchaseType,
            items,
            status: cartAddError?.status || null,
            responseData: cartAddError?.responseData || null,
          },
        });
        if (cartCleared) {
          await this._restoreCartSnapshot(previousCartSnapshot, 'bundle-rebuild-add-failed');
        }
        showCheckoutError(
          'Vaya, parece que no hemos podido preparar tu pedido... Escríbenos a hola@tinyriots.es o por WhatsApp si el problema persiste.',
          cartAddError,
          refId,
        );
        throw createCheckoutAbortError('Cart add failed during bundle rebuild', 'CART_ADD_FAILED', {
          refId,
          status: cartAddError?.status || null,
          responseData: cartAddError?.responseData || null,
          items,
        });
      }

      // Tag cart as coming from storefront (for carrier service detection)
      CartAPI.updateAttributes({ _source: 'storefront' });
      window.TR.breadcrumb('Cart rebuilt', { itemCount: items.length, hasTxnId: !!txnId }, 'state');
      } finally {
        // Release the cart mutation lock immediately on success or failure.
        // The 45s upper bound only matters if execution never reaches finally
        // (e.g. browser tab closed mid-flight) — in that case the lock expires naturally.
        window.__bundleCartLockUntil = 0;
      }
    },

    /**
     * Returns whether Shopify currently shows an active discount in the cart session.
     */
    _cartHasActiveDiscount(cartData, expectedCode) {
      if (!cartData) return false;

      const discountCodes = Array.isArray(cartData.discount_codes) ? cartData.discount_codes : [];
      const hasExpectedCode = expectedCode
        ? discountCodes.some((entry) => {
            if (typeof entry === 'string') return entry === expectedCode;
            if (entry && typeof entry.code === 'string') return entry.code === expectedCode;
            return false;
          })
        : discountCodes.length > 0;

      const hasCartDiscount = Array.isArray(cartData.cart_level_discount_applications)
        && cartData.cart_level_discount_applications.length > 0;
      const hasLineDiscount = Array.isArray(cartData.items)
        && cartData.items.some((item) =>
          (Array.isArray(item.discounts) && item.discounts.length > 0)
          || (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0)
          || (item.line_level_total_discount || 0) > 0
          || (item.total_discount || 0) > 0
        );

      return hasExpectedCode || hasCartDiscount || hasLineDiscount;
    },

    _summarizeDiscountState(cartData, expectedCode) {
      const discountCodes = Array.isArray(cartData?.discount_codes)
        ? cartData.discount_codes.map((entry) => (typeof entry === 'string' ? entry : entry?.code)).filter(Boolean)
        : [];
      const cartLevelDiscounts = Array.isArray(cartData?.cart_level_discount_applications)
        ? cartData.cart_level_discount_applications.length
        : 0;
      const discountedItems = Array.isArray(cartData?.items)
        ? cartData.items.filter((item) =>
          (Array.isArray(item.discounts) && item.discounts.length > 0)
          || (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0)
          || (item.line_level_total_discount || 0) > 0
          || (item.total_discount || 0) > 0
        ).length
        : 0;

      return {
        expectedCode,
        discountCodes,
        cartLevelDiscounts,
        discountedItems,
        totalDiscount: cartData?.total_discount || 0,
        totalPrice: cartData?.total_price || 0,
      };
    },

    async _verifyDiscountSession(expectedCode, context) {
      const attemptScheduleMs = [0, 300, 600, 900];
      const startedAt = Date.now();

      for (let i = 0; i < attemptScheduleMs.length; i += 1) {
        const targetDelay = attemptScheduleMs[i];
        const elapsedBeforeAttempt = Date.now() - startedAt;
        if (elapsedBeforeAttempt < targetDelay) {
          await new Promise((resolve) => setTimeout(resolve, targetDelay - elapsedBeforeAttempt));
        }

        try {
          const res = await fetch(`/cart.js?_=${Date.now()}-${i}`, {
            credentials: 'same-origin',
            cache: 'no-store',
          });
          const cartData = await res.json();
          const summary = this._summarizeDiscountState(cartData, expectedCode);
          const verified = this._cartHasActiveDiscount(cartData, expectedCode);
          const elapsedMs = Date.now() - startedAt;

          this._log('[BundleBuilder] Discount verify (%s) attempt %d after %dms: %o', context, i + 1, elapsedMs, summary);
          console.info('[BundleBuilder] Discount verify (%s) attempt %d', context, i + 1, {
            elapsedMs,
            summary,
          });
          window.TR.breadcrumb('Discount verify attempt', {
            context,
            attempt: i + 1,
            elapsedMs,
            expectedCode,
            discountCodes: summary.discountCodes.join(','),
            cartLevelDiscounts: summary.cartLevelDiscounts,
            discountedItems: summary.discountedItems,
            totalDiscount: summary.totalDiscount,
          }, 'state');

          if (verified) {
            return {
              verified: true,
              attempt: i + 1,
              elapsedMs,
              summary,
            };
          }
        } catch (e) {
          const elapsedMs = Date.now() - startedAt;
          this._warn('[BundleBuilder] Discount verify attempt %d failed after %dms:', i + 1, elapsedMs, e);
          console.warn('[BundleBuilder] Discount verify attempt failed', {
            attempt: i + 1,
            elapsedMs,
            error: e?.message || String(e),
          });
        }
      }

      return {
        verified: false,
        attempt: attemptScheduleMs.length,
        elapsedMs: Date.now() - startedAt,
        summary: null,
      };
    },

    /**
     * Apply the bundle discount code (if any) via fetch, then navigate.
     * Using fetch() instead of /discount/{code}?redirect= avoids polluting
     * browser history with a redirect URL that traps the back button.
     */
    async _applyDiscountAndNavigate(targetPath) {
      if (this._activeDiscountCode) {
        const code = this._activeDiscountCode;
        try {
          this._log('[BundleBuilder] Discount apply requested: code=%s target=%s', code, targetPath);
          console.info('[BundleBuilder] Discount apply requested', { code, targetPath });
          window.TR.breadcrumb('Discount apply requested', { code, targetPath }, 'request');

          const res = await fetch(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(targetPath)}`, {
            credentials: 'same-origin',
            redirect: 'manual',
            cache: 'no-store',
          });
          this._log('[BundleBuilder] Discount apply response: status=%s type=%s redirected=%s', res.status, res.type, res.redirected);
          console.info('[BundleBuilder] Discount apply response', {
            code,
            targetPath,
            status: res.status,
            type: res.type,
            redirected: res.redirected,
          });

          const verification = await this._verifyDiscountSession(code, targetPath);
          if (verification.verified) {
            persistState('bundle-builder:appliedDiscountCode', code);
            this._log('[BundleBuilder] Discount session verified before navigation: %s (attempt %d, %dms)', code, verification.attempt, verification.elapsedMs);
            console.info('[BundleBuilder] Discount session verified before navigation', {
              code,
              targetPath,
              attempt: verification.attempt,
              elapsedMs: verification.elapsedMs,
            });
            window.TR.breadcrumb('Discount session verified before navigation', {
              code,
              targetPath,
              attempt: verification.attempt,
              elapsedMs: verification.elapsedMs,
            }, 'state');
          } else {
            persistState('bundle-builder:appliedDiscountCode', '');
            this._warn('[BundleBuilder] Navigating without discount verification after %dms — cart heal may be required for %s', verification.elapsedMs, code);
            console.warn('[BundleBuilder] Navigating without discount verification; cart heal may be required', {
              code,
              targetPath,
              attempts: verification.attempt,
              elapsedMs: verification.elapsedMs,
            });
            window.TR.breadcrumb('Navigating without discount verification', {
              code,
              targetPath,
              attempts: verification.attempt,
              elapsedMs: verification.elapsedMs,
            }, 'state');
          }
        } catch (e) {
          persistState('bundle-builder:appliedDiscountCode', '');
          this._warn('[BundleBuilder] Discount apply failed, navigating anyway:', e);
          console.warn('[BundleBuilder] Discount apply failed, navigating anyway', { code, targetPath, error: e?.message || String(e) });
          window.TR.breadcrumb('Builder discount apply failed', {
            code,
            targetPath,
            error: e?.message || String(e),
          }, 'error');
        }
      }
      window.location.href = targetPath;
    },

    /**
     * Intercepts clicks on any link to /cart so bundle items are synced
     * to the Shopify cart before navigation (e.g., header cart icon).
     */
    _interceptCartNavigation() {
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href="/cart"], a[href$="/cart"]');
        if (!link || this.item_count === 0 || !this.isBundleComplete) return;

        e.preventDefault();
        this._rebuildCartForCheckout()
          .then(() => {
            this._saveCartFingerprint();
            this._saveDiscountTiers();
            safeSetItem('bundle-builder:wentToCart', '1');
            return this._applyDiscountAndNavigate('/cart');
          })
          .catch((err) => {
            if (err?.isExpected) {
              this._warn('[BundleBuilder] Cart sync aborted; staying on builder:', err);
              return;
            }
            this._warn('[BundleBuilder] Cart sync failed, navigating anyway:', err);
            window.location.href = '/cart';
          });
      });
    },

    async goToCart() {
      if (this.isAdding || this.item_count === 0 || !this.isBundleComplete) return;
      this.isAdding = true;

      try {
        await this._rebuildCartForCheckout();
        this._saveCartFingerprint();
        this._saveDiscountTiers();
        safeSetItem('bundle-builder:wentToCart', '1');
        await this._applyDiscountAndNavigate('/cart');
      } catch (error) {
        console.error('[BundleBuilder] Error going to cart:', error);
        if (!error?.isExpected) window.TR.error(error, { context: 'goToCart' });
        this.isAdding = false;
      }
    },

    async goToCheckout() {
      if (this.isAdding || this.item_count === 0 || !this.isBundleComplete) return;
      this.isAdding = true;

      try {
        await this._rebuildCartForCheckout();
        persistState('bundle-builder:items', '');
        persistState('bundle-builder:cartSync', '');
        await this._applyDiscountAndNavigate('/checkout');
      } catch (error) {
        console.error('[BundleBuilder] Error during checkout:', error);
        if (!error?.isExpected) window.TR.error(error, { context: 'goToCheckout' });
        this.isAdding = false;
      }
    },

    /* ── Debug Logging ── */
    _log(...args) { if (this._debug) console.info(...args); },
    _warn(...args) { if (this._debug) console.warn(...args); },

    /* ── Money Formatting ── */
    formatMoney,
  };
}

/* ── Register with Alpine ── */
document.addEventListener('alpine:init', () => {
  Alpine.data('bundleBuilder', bundleBuilder);
});
